import React, { useMemo, useState } from "react";
import AdminForceRender from "./AdminForceRender";
import MoviesStats from "./MoviesStats";
import SummaryCards from "./SummaryCards";
import PaymentMethodsTable from "./PaymentMethodsTable";
import CountriesTable from "./CountriesTable";
import TransactionDateFilter from "./TransactionDateFilter";

const AdminData = ({ data }) => {
  const { transactions } = data;
  
  // State for date filtering
  const [dateFilter, setDateFilter] = useState('all');
  const [customDateRange, setCustomDateRange] = useState(null);

  // Filter transactions by date using billedAt field
  const filteredTransactions = useMemo(() => {
    if (!transactions || transactions.length === 0) {
      return [];
    }

    if (dateFilter === 'all') {
      return transactions;
    }

    const now = new Date();
    let startDate, endDate;

    switch (dateFilter) {
      case 'last30days':
        // Use UTC to match billedAt timezone
        endDate = new Date();
        startDate = new Date(endDate.getTime() - (30 * 24 * 60 * 60 * 1000));
        break;
      case 'last3months':
        endDate = new Date();
        startDate = new Date(endDate.getFullYear(), endDate.getMonth() - 3, endDate.getDate());
        break;
      case 'last6months':
        endDate = new Date();
        startDate = new Date(endDate.getFullYear(), endDate.getMonth() - 6, endDate.getDate());
        break;
      case 'last12months':
        endDate = new Date();
        startDate = new Date(endDate.getFullYear(), endDate.getMonth() - 12, endDate.getDate());
        break;
      case 'custom':
        if (customDateRange && customDateRange.startDate && customDateRange.endDate) {
          startDate = new Date(customDateRange.startDate);
          endDate = new Date(customDateRange.endDate);
          // Set end date to end of day
          endDate.setHours(23, 59, 59, 999);
        } else {
          return transactions;
        }
        break;
      default:
        return transactions;
    }

    return transactions.filter(txn => {
      const billedDate = new Date(txn.billedAt?txn.billedAt:txn.billed_at);
      return billedDate >= startDate && billedDate <= endDate;
    });
  }, [transactions, dateFilter, customDateRange]);

  // Handle filter changes from the filter component
  const handleFilterChange = (filterType, customRange) => {
    setDateFilter(filterType);
    setCustomDateRange(customRange);
  };

  // Process transactions client-side
  const processedData = useMemo(() => {
    if (!filteredTransactions || filteredTransactions.length === 0) {
      return {
        countries: [],
        paymentMethods: [],
        totalTransactions: 0,
        totalRevenue: 0,
        firstTransactionDate: null
      };
    }

    const countryNames = new Intl.DisplayNames(['en'], { type: 'region' });
    const countryCounts = {};
    const paymentMethodCounts = {};
    const cardTypeCounts = {};
    let totalRevenue = 0;

    // Find the earliest transaction date from filtered transactions
    const sortedTransactions = [...filteredTransactions].sort((a, b) => new Date(a.billedAt) - new Date(b.billedAt));
    const firstTransactionDate = sortedTransactions[0]?.billedAt || null;

    // Process each filtered transaction
    filteredTransactions.forEach(txn => {
      // Calculate revenue (convert from cents to dollars if needed)
      if (txn.details && txn.details.totals && txn.details.totals.total) {
        totalRevenue += parseFloat(txn.details.totals.total) / 100; // Assuming amounts are in cents
      }

      // Country data
      const code = txn.address?.country_code || 'Unknown';
      countryCounts[code] = (countryCounts[code] || 0) + 1;
      
      // Payment method data
      if (txn.payments && txn.payments.length > 0) {
        const payment = txn.payments[0]; // Use first payment method
        if (payment.methodDetails && payment.methodDetails.type) {
          const paymentType = payment.methodDetails.type;
          paymentMethodCounts[paymentType] = (paymentMethodCounts[paymentType] || 0) + 1;
          
          // If it's a card, also track card types
          if (paymentType === 'card' && payment.methodDetails.card && payment.methodDetails.card.type) {
            const cardType = payment.methodDetails.card.type;
            cardTypeCounts[cardType] = (cardTypeCounts[cardType] || 0) + 1;
          }
        }
      }
    });

    // Convert countries to sorted array
    const sortedCountries = Object.entries(countryCounts)
      .map(([code, count]) => ({
        code,
        name: code === 'Unknown' ? 'Unknown' : countryNames.of(code),
        count
      }))
      .sort((a, b) => b.count - a.count);

    // Convert payment methods to sorted array with expandable card types
    const paymentMethods = Object.entries(paymentMethodCounts)
      .map(([type, count]) => {
        const paymentMethod = {
          type: type,
          count: count,
          percentage: filteredTransactions.length > 0 ? ((count / filteredTransactions.length) * 100).toFixed(1) : 0
        };

        // Add card subtypes if this is a card payment method
        if (type === 'card' && Object.keys(cardTypeCounts).length > 0) {
          paymentMethod.expandable = true;
          paymentMethod.subTypes = Object.entries(cardTypeCounts)
            .map(([cardType, cardCount]) => ({
              type: cardType,
              count: cardCount,
              percentage: count > 0 ? ((cardCount / count) * 100).toFixed(1) : 0
            }))
            .sort((a, b) => b.count - a.count);
        }

        return paymentMethod;
      })
      .sort((a, b) => b.count - a.count);

    return {
      countries: sortedCountries,
      paymentMethods: paymentMethods,
      totalTransactions: filteredTransactions.length,
      totalRevenue: totalRevenue,
      firstTransactionDate: firstTransactionDate
    };
  }, [filteredTransactions]);

  // Process movie families from filtered transactions
  const movieFamilies = useMemo(() => {
    if (!filteredTransactions || filteredTransactions.length === 0) {
      return {};
    }

    const familyCounts = {};
    filteredTransactions.forEach(transaction => {
      const family = transaction.family;
      if (family) {
        familyCounts[family] = (familyCounts[family] || 0) + 1;
      }
    });

    // Transform counts into arrays (to match the expected format for MoviesStats)
    const families = {};
    Object.entries(familyCounts).forEach(([family, count]) => {
      families[family] = new Array(count).fill(null); // Create array with count items
    });

    return families;
  }, [filteredTransactions]);

  const { countries, paymentMethods, totalTransactions, totalRevenue, firstTransactionDate } = processedData;

  const hasNoData =
    (!movieFamilies || Object.keys(movieFamilies).length === 0) &&
    (!countries || countries.length === 0) &&
    (!paymentMethods || paymentMethods.length === 0);

  return (
    <div className="space-y-6">
      <TransactionDateFilter
        onFilterChange={handleFilterChange}
        totalTransactions={transactions ? transactions.length : 0}
        filteredTransactions={filteredTransactions.length}
      />

      <SummaryCards
        totalTransactions={totalTransactions}
        totalRevenue={totalRevenue}
        firstTransactionDate={firstTransactionDate}
        countriesCount={countries ? countries.length : 0}
        paymentMethodsCount={paymentMethods ? paymentMethods.length : 0}
      />

      <PaymentMethodsTable paymentMethods={paymentMethods} />

      <CountriesTable
        countries={countries}
        totalTransactions={totalTransactions}
      />

      <MoviesStats movies={movieFamilies} />
      
      <AdminForceRender />

      {hasNoData && (
        <div className="bg-white rounded-lg shadow-sm p-6 text-center">
          <p className="text-gray-500">No data available</p>
        </div>
      )}
    </div>
  );
};

export default AdminData;
